import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMenuOpen]);

  const navLinks = [
    { to: "/", label: "Home" },
    { to: "/projects", label: "Projects" },
    { to: "/blog", label: "Blog" },
    { to: "/about", label: "About" },
    { to: "/contact", label: "Contact" },
  ];

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/" || location.pathname === "/index.html";
    return location.pathname === path || location.pathname.startsWith(`${path}.html`);
  };

  return (
    <nav className="sticky top-0 z-40 bg-[#0f0f0f] border-b border-[#1a1a1a] w-full">
      <div className="max-w-[1100px] mx-auto px-5 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link
          to="/"
          className="flex items-center gap-2.5 text-lg font-semibold text-[#f2f2f2] hover:text-white transition-colors group"
          style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 600 }}
          onClick={() => setIsMenuOpen(false)}
        >
          <span className="w-7 h-7 rounded bg-[#161616] border border-[#2a2a2a] flex items-center justify-center font-mono font-bold text-[#00FF94] text-sm group-hover:border-[#00FF94]/50 transition-colors">
            H
          </span>
          <span className="tracking-tight">Harshit</span>
        </Link>

        {/* Desktop Links */}
        <div className="hidden md:flex items-center space-x-8">
          {navLinks.map((link) => {
            const active = isActive(link.to);
            return (
              <Link
                key={link.to}
                to={link.to}
                className={`text-sm transition-colors ${
                  active ? "text-white font-medium" : "text-[#555555] hover:text-[#cccccc]"
                }`}
              >
                {link.label}
              </Link>
            );
          })}
        </div>

        {/* Mobile Hamburger Button */}
        <button
          className="md:hidden p-2 text-[#f2f2f2] hover:text-white focus:outline-none"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label={isMenuOpen ? "Close menu" : "Open menu"}
        >
          {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile Slide-in Drawer */}
      {isMenuOpen && (
        <div className="fixed inset-0 z-50 md:hidden flex flex-col bg-[#0f0f0f]">
          <div className="h-16 px-5 border-b border-[#1a1a1a] flex items-center justify-between">
            <Link
              to="/"
              className="flex items-center gap-2.5 text-lg font-semibold text-[#f2f2f2]"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
              onClick={() => setIsMenuOpen(false)}
            >
              <span className="w-7 h-7 rounded bg-[#161616] border border-[#2a2a2a] flex items-center justify-center font-mono font-bold text-[#00FF94] text-sm">
                H
              </span>
              <span className="tracking-tight">Harshit</span>
            </Link>
            <button
              onClick={() => setIsMenuOpen(false)}
              className="p-2 text-[#f2f2f2]"
              aria-label="Close menu"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="flex-1 px-6 py-8 flex flex-col space-y-6">
            {navLinks.map((link) => {
              const active = isActive(link.to);
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setIsMenuOpen(false)}
                  className={`text-xl transition-colors ${
                    active ? "text-white font-medium" : "text-[#555555]"
                  }`}
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  {link.label}
                </Link>
              );
            })}
          </div>

          <div className="p-6 border-t border-[#1a1a1a] text-xs text-[#555555]">
            © 2026 Harshit · Building in public
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
